import { useState, useEffect } from "react";
import { api, useAuth } from "@/App";
import { Lock, Shield, Eye, EyeOff, Check, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function SecurityTab() {
  const { user } = useAuth();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pin, setPin] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [savingPin, setSavingPin] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      api.get("/auth/has-pin").then(r => setHasPin(r.data.has_pin)).catch((e) => console.error("Échec récupération PIN:", e));
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangePassword = async () => {
    if (!currentPwd) { toast.error("Entrez votre mot de passe actuel"); return; }
    if (newPwd.length < 4) { toast.error("Le nouveau mot de passe doit contenir au moins 4 caractères"); return; }
    if (newPwd !== confirmPwd) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (isAdmin && hasPin && !pin) { toast.error("Entrez votre code de sécurité"); return; }

    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
        confirmation_code: isAdmin ? pin : undefined
      });
      toast.success("Mot de passe modifié !");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); setPin("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    }
    setSaving(false);
  };

  const handleSetPin = async () => {
    if (newPin && (newPin.length < 4 || newPin.length > 8)) {
      toast.error("Le code doit avoir entre 4 et 8 caractères");
      return;
    }
    setSavingPin(true);
    try {
      await api.put("/auth/admin-pin", { pin: newPin });
      toast.success(newPin ? "Code de sécurité mis à jour" : "Code de sécurité supprimé");
      setHasPin(!!newPin);
      setNewPin("");
    } catch (e) { toast.error("Erreur"); }
    setSavingPin(false);
  };

  return (
    <div className="space-y-6">
      {/* Change Password */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Modifier le mot de passe
          </CardTitle>
          <CardDescription>Changez votre mot de passe de connexion</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 max-w-md">
          <div className="space-y-2">
            <Label>Mot de passe actuel</Label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                placeholder="Votre mot de passe actuel"
                data-testid="current-password"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowCurrent(!showCurrent)}>
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Nouveau mot de passe (min. 4 car.)"
                data-testid="new-password"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" onClick={() => setShowNew(!showNew)}>
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Confirmer le nouveau mot de passe</Label>
            <Input
              type="password"
              value={confirmPwd}
              onChange={(e) => setConfirmPwd(e.target.value)}
              placeholder="Retapez le nouveau mot de passe"
              data-testid="confirm-password"
            />
            {confirmPwd && newPwd && (
              <p className={`text-xs ${newPwd === confirmPwd ? "text-emerald-600" : "text-red-600"}`}>
                {newPwd === confirmPwd ? "Les mots de passe correspondent" : "Les mots de passe ne correspondent pas"}
              </p>
            )}
          </div>

          {/* Admin 2FA code */}
          {isAdmin && hasPin && (
            <div className="space-y-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Label className="flex items-center gap-2 text-amber-800">
                <Shield className="h-4 w-4" />
                Code de sécurité (double authentification)
              </Label>
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Votre code PIN"
                className="max-w-[200px]"
                data-testid="security-pin"
              />
            </div>
          )}

          <Button onClick={handleChangePassword} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="save-password-btn">
            {saving ? <div className="spinner" /> : <><Lock className="h-4 w-4 mr-2" />Modifier le mot de passe</>}
          </Button>
        </CardContent>
      </Card>

      {/* Admin 2FA PIN Setup */}
      {isAdmin && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-indigo-800">
              <Shield className="h-5 w-5" />
              Double authentification (Admin)
            </CardTitle>
            <CardDescription>
              {hasPin 
                ? "Un code de sécurité est configuré. Il sera demandé pour changer votre mot de passe." 
                : "Configurez un code PIN pour sécuriser le changement de mot de passe admin."
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                {hasPin ? "Modifier le code PIN" : "Créer un code PIN"}
              </Label>
              <Input
                type="password"
                value={newPin}
                onChange={(e) => setNewPin(e.target.value)}
                placeholder="Code PIN (4-8 caractères)"
                className="max-w-[200px]"
                data-testid="new-pin-input"
              />
              <p className="text-xs text-slate-500">Laissez vide pour supprimer le code de sécurité</p>
            </div>
            <Button onClick={handleSetPin} disabled={savingPin} variant="outline" className="border-indigo-300 text-indigo-700" data-testid="save-pin-btn">
              {savingPin ? <div className="spinner" /> : <><Shield className="h-4 w-4 mr-2" />{hasPin ? "Modifier le code" : "Activer la double auth"}</>}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
